import type { ManageableWorkspace } from '@/contexts/ProjectContext';

type HybridOnboardingTargetInput = {
  hasProject: boolean;
  forceNewProject?: boolean;
  canCreatePersonalProject: boolean;
  manageableWorkspaces: ManageableWorkspace[];
  requestedWorkspaceId?: string | null;
};

export type OnboardingWorkspaceIntent = 'create' | 'continue' | null;

export type HybridOnboardingTarget = {
  href: string;
  workspaceId: string | null;
  workspaceName: string | null;
};

export type OnboardingCompletionReceipt = {
  projectId: string;
  workflowId: string | null;
};

type OnboardingToolReceiptInput = {
  status: 'preparing' | 'calling' | 'done' | 'error';
  metadata?: Record<string, unknown>;
};

export function getOnboardingWorkspaceIntent(params: {
  newProjectRequested: boolean;
  continueOnboardingRequested: boolean;
  resetRequested: boolean;
}): OnboardingWorkspaceIntent {
  if (params.newProjectRequested || params.resetRequested) return 'create';
  if (params.continueOnboardingRequested) return 'continue';
  return null;
}

export function getReusableOnboardingSessionProjectId(params: {
  sessionId: string | null;
  deepLinkSessionId: string | null;
  mode?: string | null;
  projectId?: string | null;
}): string | null {
  if (
    params.mode !== 'onboarding' ||
    !params.sessionId ||
    params.sessionId !== params.deepLinkSessionId
  ) {
    return null;
  }
  return typeof params.projectId === 'string' && params.projectId.trim().length > 0
    ? params.projectId.trim()
    : null;
}

export function getOnboardingCompletionReceipt(
  tools: OnboardingToolReceiptInput[],
  expectedProjectId: string | null
): OnboardingCompletionReceipt | null {
  if (!expectedProjectId) return null;

  for (const tool of [...tools].reverse()) {
    if (tool.status !== 'done') continue;
    const metadata = tool.metadata;
    if (
      !metadata ||
      (metadata.attuneTool !== 'projects.create' && metadata.attuneTool !== 'projects.resume')
    ) {
      continue;
    }
    const projectId =
      typeof metadata.onboardingCreatedProjectId === 'string'
        ? metadata.onboardingCreatedProjectId.trim()
        : '';
    const workflowId =
      typeof metadata.onboardingWorkflowId === 'string' ? metadata.onboardingWorkflowId.trim() : '';
    const blankWorkspace =
      metadata.attuneTool === 'projects.create' &&
      metadata.onboardingSamplerState === 'not_configured';
    if (projectId === expectedProjectId && (workflowId || blankWorkspace)) {
      return { projectId, workflowId: workflowId || null };
    }
  }

  return null;
}

export function getCompletedOnboardingWorkspaceHref(sessionId: string): string {
  const params = new URLSearchParams({ session: sessionId });
  return `/app/workspace?${params.toString()}`;
}

function findManageableWorkspace(
  manageableWorkspaces: ManageableWorkspace[],
  workspaceId?: string | null,
) {
  if (!workspaceId) return null;
  return manageableWorkspaces.find(workspace => workspace.id === workspaceId) || null;
}

export function getHybridOnboardingTarget({
  hasProject,
  forceNewProject,
  canCreatePersonalProject,
  manageableWorkspaces,
  requestedWorkspaceId,
}: HybridOnboardingTargetInput): HybridOnboardingTarget | null {
  if (hasProject && !forceNewProject) return null;

  const requestedWorkspace = findManageableWorkspace(manageableWorkspaces, requestedWorkspaceId);
  if (requestedWorkspaceId && !requestedWorkspace) return null;
  const preferredWorkspace = requestedWorkspace || manageableWorkspaces[0] || null;

  if (!canCreatePersonalProject && !preferredWorkspace) return null;

  const params = new URLSearchParams();
  params.set('new_project', 'true');
  if (preferredWorkspace) {
    params.set('workspace_id', preferredWorkspace.id);
  }
  const query = params.toString();

  return {
    href: `/app/workspace${query ? `?${query}` : ''}`,
    workspaceId: preferredWorkspace?.id || null,
    workspaceName: preferredWorkspace?.name || null,
  };
}

export function getCreateProjectHref(input: HybridOnboardingTargetInput) {
  const hybridTarget = getHybridOnboardingTarget({
    ...input,
    forceNewProject: true,
  });
  if (hybridTarget) return hybridTarget.href;

  const params = new URLSearchParams({ new_project: 'true' });
  if (input.requestedWorkspaceId) {
    params.set('workspace_id', input.requestedWorkspaceId);
  }

  return `/app/workspace?${params.toString()}`;
}
